import { useEffect, useState } from "react";
import { Form, notification } from "top-component-library";
import { getEmployees, saveSupportAlerts } from "util/ApiCalls";

import { ALERT_LABELS, EMAIL_ALERT_MAP, PHONE_ALERT_MAP } from "../supportAlertConstants";

// Owns all Add/Edit Staff Alert state, the employee load + edit-seed effects, the alert-list
// mutations (including the alert_class string parsing), the derived contact-method/alert options,
// and the save payload. Keeps the step components purely presentational.
export const useStaffAlertForm = ({ selectedCustomer, alertToEdit, copyableRecipients, onSaveOrEditCallback }) => {
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [recipientToAdd, setRecipientToAdd] = useState({ employeeOrContact: "employee" });
  const [alertsToAdd, setAlertsToAdd] = useState([]);
  const [userFlow, setUserFlow] = useState("customAlerts");
  const [copyStaffId, setCopyStaffId] = useState(null);
  const [contactForm] = Form.useForm();

  const setEmployeeData = async () => {
    setIsLoading(true);
    const res = await getEmployees(selectedCustomer);
    setEmployees(res);
    setIsLoading(false);
  };

  useEffect(() => {
    setEmployeeData();
  }, [selectedCustomer]); // eslint-disable-line react-hooks/exhaustive-deps

  // Seeds a builder alert from either a picked alert class (new) or an existing alert (edit). The
  // charAt() parsing mirrors how the alert_class strings encode their trigger times server-side.
  const addAlert = (alertClass, existingAlert = null) => {
    const alertType = existingAlert?.alert_class || alertClass;
    const res = {
      alert_class: alertType,
      stationSetting: !existingAlert || existingAlert.stations.length === 0 ? "all" : "specific",
      stations: existingAlert ? existingAlert.stations : [],
      snooze_till_time: existingAlert?.snooze_till_time,
    };
    if (alertType.startsWith("catering_order_unopened")) {
      res.alert_class = "catering_order_unopened";
      res.hours = existingAlert ? existingAlert.alert_class.charAt(28) : "1";
    } else if (alertType.startsWith("email_catering_order_in")) {
      res.alert_class = "email_catering_order_in";
      if (existingAlert?.alert_class.charAt(24) === "3") {
        res.minutes = "30";
        res.timePicker = "minutes";
      } else {
        res.hours = existingAlert?.hours || "4";
        res.timePicker = "hours";
      }
    } else if (alertType.startsWith("order_unopened")) {
      res.alert_class = "order_unopened";
      res.minutes = existingAlert ? existingAlert.alert_class.charAt(19) : "5";
      if (!isNaN(parseInt(existingAlert?.alert_class.charAt(20), 10))) {
        res.minutes = res.minutes + existingAlert.alert_class.charAt(20);
      }
    }
    setAlertsToAdd((current) => current.concat([res]));
  };

  useEffect(() => {
    if (alertToEdit) {
      const recipientInitialValues = alertToEdit.user
        ? {
            employeeOrContact: "employee",
            employee: alertToEdit.user,
            staffEmail: alertToEdit.email || "",
            staffPhone: alertToEdit.phone || "",
          }
        : {
            employeeOrContact: "new",
            newName: alertToEdit.name,
            newEmail: alertToEdit.email || "",
            newPhone: alertToEdit.phone || "",
          };
      contactForm.setFieldsValue(recipientInitialValues);
      setRecipientToAdd(recipientInitialValues);
      alertToEdit.support_alerts.forEach((existingAlert) => addAlert("", existingAlert));
    }
  }, [alertToEdit]); // eslint-disable-line react-hooks/exhaustive-deps

  const employeeArray = employees.map((employee) => ({
    label: `${employee.user.first_name} ${employee.user.last_name}`,
    value: employee.user.id,
  }));

  const validContactMethods =
    recipientToAdd.employeeOrContact === "employee" && recipientToAdd.staffPhone
      ? "both"
      : recipientToAdd.employeeOrContact === "employee"
      ? "email"
      : recipientToAdd.newEmail && recipientToAdd.newPhone
      ? "both"
      : recipientToAdd.newEmail
      ? "email"
      : "phone";

  const availableAlerts = Object.entries(
    validContactMethods === "both" ? ALERT_LABELS : validContactMethods === "email" ? EMAIL_ALERT_MAP : PHONE_ALERT_MAP
  ).map(([value, label]) => ({ label, value }));

  const selectEmployee = (employeeId) => {
    const employee = employees.find((e) => e.user.id === employeeId);
    if (!employee) return;
    const email = employee.user.email;
    contactForm.setFieldsValue({ staffEmail: email });
    setRecipientToAdd((current) => ({ ...current, employee: employeeId, staffEmail: email }));
  };

  const changeAlertOption = (alert, index, newValues) => {
    setAlertsToAdd((current) =>
      current.slice(0, index).concat([{ ...alert, ...newValues }].concat(current.slice(index + 1)))
    );
  };

  const removeAlert = (index) => {
    setAlertsToAdd((current) => current.filter((_, i) => i !== index));
  };

  const save = async (editMode) => {
    const isEmployee = recipientToAdd.employeeOrContact === "employee";
    const employee = isEmployee ? employees.find((e) => e.user.id === recipientToAdd.employee) : null;
    if (isEmployee && !employee) {
      notification.error({ message: "Please select an employee before saving." });
      return;
    }
    // Editing always rewrites the full custom alert set; the preset/copy flows are add-only.
    const effectiveFlow = editMode ? "customAlerts" : userFlow;
    const properties = {
      alertSettings: effectiveFlow,
      createNew: !editMode,
      isCustomer: true,
      name: isEmployee ? `${employee.user.first_name} ${employee.user.last_name}` : recipientToAdd.newName,
    };
    if (effectiveFlow === "customAlerts") properties.newAlerts = alertsToAdd;
    if (effectiveFlow === "copyExistingAlerts") properties.copyStaffId = copyStaffId;
    if (editMode) properties.staffId = alertToEdit.id;
    if (isEmployee) {
      properties.employeeId = employee.user.id;
      properties.email = recipientToAdd.staffEmail;
      if (recipientToAdd.staffPhone) properties.phone = recipientToAdd.staffPhone;
    } else {
      if (recipientToAdd.newEmail) properties.email = recipientToAdd.newEmail;
      if (recipientToAdd.newPhone) properties.phone = recipientToAdd.newPhone;
    }
    await saveSupportAlerts(selectedCustomer, properties);
    onSaveOrEditCallback();
    notification.success({ message: "Staff alert successfully saved." });
  };

  return {
    contactForm,
    employees,
    isLoading,
    recipientToAdd,
    setRecipientToAdd,
    alertsToAdd,
    userFlow,
    setUserFlow,
    copyStaffId,
    setCopyStaffId,
    employeeArray,
    validContactMethods,
    availableAlerts,
    copyableRecipients,
    selectEmployee,
    addAlert,
    changeAlertOption,
    removeAlert,
    save,
  };
};
